import type { GameSettings, GameState, ItemId, Scalar } from '../types';

export const SAVE_SCHEMA_VERSION = 1;

export function createInitialState(): GameState {
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    campaignId: 'vejle-historisk-adventure-demo',
    currentLocationId: null,
    currentSceneId: null,
    inventory: [],
    selectedItemId: null,
    flags: { starterLunchRemoved: true },
    sceneState: {},
    visitedLocations: [],
    completedPuzzles: [],
    unlockedLocations: ['raadhustorvet'],
    settings: {
      musicVolume: 0.45,
      sfxVolume: 0.65,
      muted: false,
      reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    },
    lastSavedAt: new Date(0).toISOString(),
  };
}

type StateListener = (state: Readonly<GameState>) => void;

export class GameStateStore {
  readonly #listeners = new Set<StateListener>();
  #state: GameState;

  constructor(initialState: GameState = createInitialState()) {
    this.#state = structuredClone(initialState);
  }

  get snapshot(): Readonly<GameState> {
    return this.#state;
  }

  replace(state: GameState): void {
    this.#state = structuredClone(state);
    this.#notify();
  }

  update(mutator: (draft: GameState) => void): void {
    const draft = structuredClone(this.#state);
    mutator(draft);
    draft.lastSavedAt = new Date().toISOString();
    this.#state = draft;
    this.#notify();
  }

  addItem(itemId: ItemId): boolean {
    if (this.#state.inventory.includes(itemId)) return false;
    this.update((draft) => draft.inventory.push(itemId));
    return true;
  }

  removeItem(itemId: ItemId): boolean {
    if (!this.#state.inventory.includes(itemId)) return false;
    this.update((draft) => {
      draft.inventory = draft.inventory.filter((id) => id !== itemId);
      if (draft.selectedItemId === itemId) draft.selectedItemId = null;
    });
    return true;
  }

  setFlag(key: string, value: Scalar): void {
    this.update((draft) => {
      draft.flags[key] = value;
    });
  }

  setSceneState(sceneId: string, key: string, value: Scalar): void {
    this.update((draft) => {
      const scene = (draft.sceneState[sceneId] ??= {});
      scene[key] = value;
    });
  }

  updateSettings(settings: Partial<GameSettings>): void {
    this.update((draft) => Object.assign(draft.settings, settings));
  }

  subscribe(listener: StateListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify(): void {
    for (const listener of this.#listeners) listener(this.#state);
  }
}
